/**
 * Per-brand SKU counts, shared by the Brands, BrandDetail and Platforms pages.
 *
 * Three switches decide whether a SKU is actually scraped and they are easy to conflate,
 * so the vocabulary is fixed here and every page reuses it rather than inventing its own:
 *
 *   total    - active SKUs carrying the brand label. The brand's footprint on a platform.
 *              Ignores every operator toggle.
 *   enabled  - `total` minus SKUs switched off via the per-SKU master toggle. What a sweep
 *              would probe, ASSUMING the brand itself is enabled.
 *   scraping - `enabled`, but zero when the brand is disabled. The bottom line: how many
 *              SKUs this brand actually contributes to a run today.
 *
 * `enabled` deliberately does not fold in the brand toggle: a disabled brand should read
 * as "N SKUs, brand off", not as a bare 0 that looks like a discovery failure.
 */
import { skuCountsV1BrandsSkuCountsGet } from "./generated/qcsControlPlane";
import type { SkuCount } from "./generated/model";
import { platformKeys } from "./platforms";
import type { Platform } from "./labels";

/** platform -> brand -> counts. */
export type SkuCountsByPlatform = Record<string, Record<string, SkuCount>>;

const ZERO: SkuCount = { enabled: 0, total: 0 };

/** Fetch every platform's per-brand counts in parallel. */
export async function fetchSkuCounts(includeCombos: boolean): Promise<SkuCountsByPlatform> {
  const pairs = await Promise.all(
    platformKeys().map(
      async (p) =>
        [p, await skuCountsV1BrandsSkuCountsGet({ platform: p, include_combos: includeCombos })] as const,
    ),
  );
  return Object.fromEntries(pairs) as SkuCountsByPlatform;
}

/** One brand's counts on one platform; zeroes when the brand has no SKUs there. */
export function countFor(
  data: SkuCountsByPlatform | undefined,
  platform: Platform | string,
  brand: string,
): SkuCount {
  return data?.[platform]?.[brand] ?? ZERO;
}
